/* eslint-disable no-restricted-imports */
import * as React from "react";
import type { PaletteMode } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { createMuiTheme } from "theme";
import { ThemeApplier } from "./ThemeApplier";
import type { ChangeThemePalette } from "./ThemePaletteProvider";
import { ThemePaletteProvider, useThemePalette } from "./ThemePaletteProvider";
import { DarkTheme, LightTheme } from "./Themes";
import type { OctopusTheme } from "./Themes";

type ThemeProps = React.PropsWithChildren<{
  palette: PaletteMode;
  onChangePalette: ChangeThemePalette;
}>;

function Theme({ palette, onChangePalette, children }: ThemeProps) {
  const theme = mapPaletteToTheme(palette);
  const muiTheme = React.useMemo(() => createMuiTheme(palette, theme), [palette, theme]);

  return (
    <ThemePaletteProvider palette={palette} onChangePalette={onChangePalette}>
      <ThemeApplier />
      <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
    </ThemePaletteProvider>
  );
}

function mapPaletteToTheme(themeName: string): OctopusTheme {
  return themeName === "light" ? LightTheme : DarkTheme;
}

function withTheme(render: (theme: OctopusTheme) => React.ReactElement | null | undefined): React.ReactElement {
  return <UsingTheme>{(theme) => render(theme)}</UsingTheme>;
}

type UsingThemeProps = {
  children: (theme: OctopusTheme) => React.ReactNode;
};

function UsingTheme({ children }: UsingThemeProps) {
  const theme = useOctopusTheme();
  return <>{children(theme)}</>;
}

function useOctopusTheme(): OctopusTheme {
  const [palette] = useThemePalette();
  return mapPaletteToTheme(palette);
}

export default Theme;

export { Theme, withTheme, useOctopusTheme };
